import { SiteHeader } from "@/components/site-header"
import { Link } from "react-router-dom"
import { ArrowLeftIcon } from "lucide-react"

/**
 * /docs/importacion-facturacion
 *
 * Documents the billing import feature available in Cargas → Facturación.
 * Covers: CSV format, frontend parse flow, API endpoints, bulk overrides,
 * technical architecture and known limits.
 */
export default function BillingImportGuidePage() {
  return (
    <>
      <SiteHeader title="Importación masiva de facturación" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="space-y-8 max-w-3xl">

          {/* ─── Intro ─── */}
          <div>
            <Link
              to="/docs"
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-4"
            >
              <ArrowLeftIcon className="size-3" /> Documentación
            </Link>
            <h2 className="text-base font-semibold mb-1">Importación masiva de facturación</h2>
            <p className="text-sm text-muted-foreground">
              Permite actualizar en un solo paso el estado de facturación, las observaciones y las
              etiquetas de cientos de cargas a partir de un archivo CSV o TSV. Disponible en{" "}
              <strong>Cargas → Facturación</strong> para roles <code className="font-mono text-xs">admin</code> y{" "}
              <code className="font-mono text-xs">operator</code>.
            </p>
          </div>

          {/* ─── Flujo ─── */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold border-b pb-1">Flujo paso a paso</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>El usuario sube un archivo CSV o TSV desde el botón <strong className="text-foreground">Importar facturación</strong>.</li>
              <li>El frontend parsea el archivo en memoria (sin subirlo al servidor) y llama a <code className="font-mono text-xs">POST /api/shipments/billing-import</code> con <code className="font-mono text-xs">preview: true</code>.</li>
              <li>La API resuelve los <code className="font-mono text-xs">internalId</code> a UUIDs y devuelve los encontrados (con su <code className="font-mono text-xs">billingStatus</code> actual) y los no encontrados.</li>
              <li>El frontend muestra la tabla de preview: errores primero (rojo), encontrados después. El usuario debe eliminar las filas en rojo antes de poder confirmar.</li>
              <li>Opcionalmente el usuario activa <strong className="text-foreground">Actualización masiva</strong> para aplicar un mismo estado, observaciones o tags a todas las cargas del archivo.</li>
              <li>Al confirmar, el frontend llama a <code className="font-mono text-xs">POST /api/shipments/billing-import</code> sin el flag <code className="font-mono text-xs">preview</code>. La API aplica los cambios en una transacción bulk y devuelve <code className="font-mono text-xs">{"{ updated: N }"}</code>.</li>
              <li>La tabla se refresca inmediatamente (<code className="font-mono text-xs">refetchQueries</code> awaited).</li>
            </ol>
          </section>

          {/* ─── Formato del archivo ─── */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold border-b pb-1">Formato del archivo</h3>
            <p className="text-sm text-muted-foreground">
              El sistema detecta automáticamente si el separador es coma (<code className="font-mono text-xs">,</code>) o tabulación (<code className="font-mono text-xs">\t</code>).
              Las filas cuya primera columna no sea un número entero positivo se ignoran silenciosamente (encabezados, títulos, filas vacías).
            </p>
            <div className="rounded-lg border overflow-hidden text-xs">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Columna</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Requerida</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Descripción</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Ejemplo</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-3 py-2 font-mono">Col 1 — ID</td>
                    <td className="px-3 py-2 text-emerald-600 font-medium">Sí</td>
                    <td className="px-3 py-2 text-muted-foreground">Número interno de la carga (<code className="font-mono">#N</code>)</td>
                    <td className="px-3 py-2 font-mono text-muted-foreground">697</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">Col 2 — Observaciones</td>
                    <td className="px-3 py-2 text-muted-foreground">No</td>
                    <td className="px-3 py-2 text-muted-foreground">Texto libre. Reemplaza completamente las observaciones anteriores. Vacío = sin cambio.</td>
                    <td className="px-3 py-2 font-mono text-muted-foreground">Factura pendiente</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">Col 3 — Tags</td>
                    <td className="px-3 py-2 text-muted-foreground">No</td>
                    <td className="px-3 py-2 text-muted-foreground">Tags separados por <code className="font-mono">|</code>. Reemplaza completamente los tags anteriores. Vacío = sin cambio.</td>
                    <td className="px-3 py-2 font-mono text-muted-foreground">lote-1|urgente</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="rounded-lg border bg-muted/20 px-4 py-3 space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Ejemplo de archivo</p>
              <pre className="text-xs font-mono text-foreground leading-relaxed">{`ID,Observaciones,Tags
697,Factura pendiente de revisión,urgente|prioridad
694,,lote-44
323,Sin observaciones,
1042,,`}</pre>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 px-4 py-3 space-y-1">
              <p className="text-xs font-medium text-amber-700 dark:text-amber-400">Comportamiento de reemplazo</p>
              <p className="text-xs text-amber-700/80 dark:text-amber-400/80">
                Tanto las observaciones como los tags <strong>siempre reemplazan</strong> los valores anteriores —
                nunca se acumulan. Si una fila del CSV tiene la columna vacía, el valor anterior de ese campo
                se conserva sin cambios.
              </p>
            </div>
          </section>

          {/* ─── Actualización masiva ─── */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold border-b pb-1">Actualización masiva (bulk overrides)</h3>
            <p className="text-sm text-muted-foreground">
              Al tildar <strong>Actualización masiva</strong> en el dialog, los campos del panel
              sobreescriben los valores individuales del CSV para <em>todas</em> las cargas encontradas:
            </p>
            <div className="rounded-lg border overflow-hidden text-xs">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Campo</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Comportamiento</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-3 py-2 font-mono">Estado de facturación</td>
                    <td className="px-3 py-2 text-muted-foreground">Si se selecciona, se aplica a todas. Si se deja en "Sin cambio de estado", se conserva el estado individual de cada carga. También se propaga a todos los viajes asociados.</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">Observaciones masivas</td>
                    <td className="px-3 py-2 text-muted-foreground">Si se completa, sobreescribe las observaciones de la columna 2 del CSV para todas las cargas.</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">Tags masivos</td>
                    <td className="px-3 py-2 text-muted-foreground">Si se completa (separados por <code className="font-mono">|</code>), sobreescribe los tags de la columna 3 del CSV para todas las cargas.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* ─── API ─── */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold border-b pb-1">API — <code className="font-mono text-sm">POST /api/shipments/billing-import</code></h3>

            <div className="space-y-4 text-sm">
              <div>
                <p className="font-medium mb-1">Modo preview (<code className="font-mono text-xs">preview: true</code>)</p>
                <p className="text-muted-foreground text-xs mb-2">Solo resuelve IDs. No escribe en la DB. Devuelve encontrados con su <code className="font-mono">billingStatus</code> actual y no encontrados.</p>
                <pre className="rounded-md bg-muted/50 px-4 py-3 text-xs font-mono overflow-x-auto">{`// Request
POST /api/shipments/billing-import
{
  "rows": [
    { "internalId": 697, "billingNotes": "Nota", "billingTags": ["tag1"] },
    { "internalId": 999 }
  ],
  "preview": true
}

// Response
{
  "found": [
    { "internalId": 697, "billingStatus": "liquidado" }
  ],
  "notFound": [999]
}`}</pre>
              </div>

              <div>
                <p className="font-medium mb-1">Modo confirmar</p>
                <p className="text-muted-foreground text-xs mb-2">
                  Aplica los cambios. Falla con <code className="font-mono text-xs">422</code> si algún ID no existe (segunda línea de defensa).
                  Los bulk overrides tienen precedencia sobre los valores por fila.
                </p>
                <pre className="rounded-md bg-muted/50 px-4 py-3 text-xs font-mono overflow-x-auto">{`// Request
POST /api/shipments/billing-import
{
  "rows": [
    { "internalId": 697, "billingNotes": "Nota A", "billingTags": ["tag1"] },
    { "internalId": 694, "billingNotes": null, "billingTags": null }
  ],
  // Bulk overrides (opcional) — tienen precedencia sobre valores por fila
  "bulkBillingStatus": "liquidado",
  "bulkBillingNotes": "Lote mayo",
  "bulkBillingTags": ["mayo", "revisado"]
}

// Response
{ "updated": 2 }`}</pre>
              </div>

              <div>
                <p className="font-medium mb-1">Schema de validación (<code className="font-mono text-xs">billingImportSchema</code>)</p>
                <ul className="list-disc list-inside space-y-1 text-xs text-muted-foreground">
                  <li><code className="font-mono">rows</code>: mínimo 1, máximo 1000 filas.</li>
                  <li><code className="font-mono">internalId</code>: entero positivo requerido.</li>
                  <li><code className="font-mono">billingNotes</code>: texto libre, máximo 1000 caracteres, nullable.</li>
                  <li><code className="font-mono">billingTags</code>: array de strings, cada tag entre 1 y 100 caracteres, nullable.</li>
                  <li><code className="font-mono">bulkBillingStatus</code>: enum <code className="font-mono">controlado | liquidado | facturado | cerrado | null</code>. El valor <code className="font-mono">pendiente</code> no existe en DB — usar <code className="font-mono">null</code>.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* ─── Arquitectura técnica ─── */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold border-b pb-1">Arquitectura técnica</h3>
            <div className="space-y-3 text-sm text-muted-foreground">
              <div>
                <p className="font-medium text-foreground text-xs mb-1">Parseo en el browser</p>
                <p className="text-xs">El archivo nunca se sube al servidor. El frontend lo lee con <code className="font-mono text-xs">File.text()</code>, detecta el delimitador (coma vs. tabulación contando cuál aparece más en la primera línea) y parsea con <code className="font-mono text-xs">parseCSVRow()</code> de <code className="font-mono text-xs">lib/csv.ts</code>. Las filas con primera columna no numérica se ignoran silenciosamente.</p>
              </div>
              <div>
                <p className="font-medium text-foreground text-xs mb-1">Transacción bulk — sin N+1</p>
                <p className="text-xs">Independientemente de cuántas filas vengan, la transacción siempre ejecuta un número fijo de queries:</p>
                <ol className="list-decimal list-inside space-y-1 text-xs mt-1">
                  <li><code className="font-mono">SELECT</code> — resuelve todos los internalIds a UUIDs en una sola query.</li>
                  <li><code className="font-mono">UPDATE shipments</code> — agrupa filas por valor de <code className="font-mono">billingNotes</code> y hace un UPDATE por grupo (en bulk mode: 1 sola query para todos).</li>
                  <li><code className="font-mono">DELETE shipment_billing_tags</code> — elimina todos los tags de todas las cargas afectadas en una query.</li>
                  <li><code className="font-mono">INSERT shipment_billing_tags</code> — inserta todos los tags nuevos en una sola query masiva.</li>
                  <li><code className="font-mono">UPDATE trips</code> — cascade de <code className="font-mono">billingStatus</code> a todos los viajes asociados en una query (solo si se envía <code className="font-mono">bulkBillingStatus</code>).</li>
                </ol>
              </div>
              <div>
                <p className="font-medium text-foreground text-xs mb-1">Refresh inmediato</p>
                <p className="text-xs">Al confirmar, el frontend ejecuta <code className="font-mono text-xs">await qc.refetchQueries({"{ queryKey: ['shipments-billing'], type: 'active' }"})</code> antes de mostrar el toast, asegurando que la tabla esté actualizada cuando el usuario la vea.</p>
              </div>
            </div>
          </section>

          {/* ─── Límites ─── */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold border-b pb-1">Límites y consideraciones</h3>
            <div className="rounded-lg border overflow-hidden text-xs">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Límite</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Valor</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Razón</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-3 py-2">Filas por importación</td>
                    <td className="px-3 py-2 font-mono">1000</td>
                    <td className="px-3 py-2 text-muted-foreground">El endpoint valida con Zod. Superar el límite genera un error en el frontend antes de llamar al servidor.</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2">Largo de observaciones</td>
                    <td className="px-3 py-2 font-mono">1000 chars</td>
                    <td className="px-3 py-2 text-muted-foreground">Validado en Zod. La columna <code className="font-mono">billing_notes</code> es <code className="font-mono">text</code> en PostgreSQL (sin límite real).</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2">Largo por tag</td>
                    <td className="px-3 py-2 font-mono">100 chars</td>
                    <td className="px-3 py-2 text-muted-foreground">Consistente con el campo <code className="font-mono">label varchar(100)</code> de <code className="font-mono">shipment_billing_tags</code>.</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2">Paginación del preview</td>
                    <td className="px-3 py-2 font-mono">50 filas/pág</td>
                    <td className="px-3 py-2 text-muted-foreground">Las filas con error siempre aparecen en la primera página.</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2">No requiere migración</td>
                    <td className="px-3 py-2 font-mono">—</td>
                    <td className="px-3 py-2 text-muted-foreground">Todas las columnas necesarias (<code className="font-mono">billing_notes</code>, <code className="font-mono">billing_status</code>, tabla <code className="font-mono">shipment_billing_tags</code>) ya existen en la DB.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* ─── Archivos involucrados ─── */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold border-b pb-1">Archivos involucrados</h3>
            <div className="rounded-lg border overflow-hidden text-xs">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Archivo</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Rol</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-3 py-2 font-mono">apps/api/src/routes/shipments/index.ts</td>
                    <td className="px-3 py-2 text-muted-foreground">Endpoint <code className="font-mono">POST /billing-import</code> y extensión de <code className="font-mono">PATCH /bulk-status</code></td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">packages/shared/src/schemas/shipments.ts</td>
                    <td className="px-3 py-2 text-muted-foreground"><code className="font-mono">billingImportRowSchema</code> y <code className="font-mono">billingImportSchema</code></td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">apps/web/src/pages/shipments/BillingImportDialog.tsx</td>
                    <td className="px-3 py-2 text-muted-foreground">Componente del dialog — parseo, preview, bulk overrides, paginación</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">apps/web/src/pages/shipments/ShipmentsBillingPage.tsx</td>
                    <td className="px-3 py-2 text-muted-foreground">Botón de apertura del dialog en la toolbar</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">apps/api/src/routes/shipments/shipments.test.ts</td>
                    <td className="px-3 py-2 text-muted-foreground">25 tests de schemas y routing (108 total)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

        </div>
      </div>
    </>
  )
}
